/**
 * Which years a project actually covers, shared by client and server.
 *
 * A project's term (`start_date` … `end_date`) is the only thing that makes a year meaningful:
 * entries outside it are rejected by T2/T3 (PRD §6.4), so a year outside it can never hold data.
 * Year pickers offer exactly these years and the API rejects the rest.
 */

import { berlinToday, type BusinessDate } from "./dates";

export interface ProjectTerm {
  startDate: BusinessDate;
  endDate: BusinessDate;
}

function yearOf(date: BusinessDate): number {
  return Number(date.slice(0, 4));
}

function monthOfDate(date: BusinessDate): number {
  return Number(date.slice(5, 7));
}

function currentYear(): number {
  return yearOf(berlinToday());
}

/** First and last year a picker should offer for the inclusive term. */
export function projectYearBounds(
  startDate: BusinessDate,
  endDate: BusinessDate,
  _todayYear: number = currentYear(),
): { from: number; to: number } {
  const from = yearOf(startDate);
  const to = Math.max(from, yearOf(endDate));
  return { from, to };
}

/** Every year of the term, newest first. */
export function projectYears(
  startDate: BusinessDate,
  endDate: BusinessDate,
  todayYear: number = currentYear(),
): number[] {
  const { from, to } = projectYearBounds(startDate, endDate, todayYear);
  const years: number[] = [];
  for (let year = to; year >= from; year -= 1) years.push(year);
  return years;
}

/** Every year touched by any of the projects, newest first; empty for an empty list. */
export function unionYears(projects: ProjectTerm[], todayYear: number = currentYear()): number[] {
  const years = new Set<number>();
  for (const project of projects) {
    for (const year of projectYears(project.startDate, project.endDate, todayYear)) {
      years.add(year);
    }
  }
  return [...years].sort((a, b) => b - a);
}

/** Does the year overlap the inclusive term at all? */
export function isYearInProject(
  year: number,
  startDate: BusinessDate,
  endDate: BusinessDate,
): boolean {
  if (year < yearOf(startDate)) return false;
  return year <= yearOf(endDate);
}

/**
 * Does the year touch the inclusive window `from` … `to`? Used to drop people from year-scoped
 * lists when their project assignment only starts in a later year.
 */
export function yearOverlapsWindow(year: number, from: BusinessDate, to: BusinessDate): boolean {
  if (yearOf(from) > year) return false;
  return yearOf(to) >= year;
}

/** The months (1–12) of that year which overlap the term; empty when the year is outside it. */
export function projectMonthsInYear(
  year: number,
  startDate: BusinessDate,
  endDate: BusinessDate,
): number[] {
  if (!isYearInProject(year, startDate, endDate)) return [];
  const first = year === yearOf(startDate) ? monthOfDate(startDate) : 1;
  const last = year === yearOf(endDate) ? monthOfDate(endDate) : 12;
  const months: number[] = [];
  for (let month = first; month <= last; month += 1) months.push(month);
  return months;
}

/**
 * The offered year closest to the wanted one, so a stale selection lands somewhere real instead
 * of querying a year the list no longer has. Ties go to the newer year.
 */
export function clampToYears(year: number, years: number[]): number {
  let best: number | null = null;
  for (const candidate of years) {
    if (best === null || Math.abs(candidate - year) < Math.abs(best - year)) best = candidate;
  }
  return best ?? year;
}
